// Eskom PARCC — report deck sections (owner feedback point 4).
//
// Pure builders for the "Audit Recovery Program" deck's summary tables —
// population / reviewed / gaps / compliant / self-declared / outstanding — rolled
// up by dataset, by operating unit (division), and by quarter, plus the
// PFMA/Policy/Housekeeping gap-tier split and the Potential-IE Rand exposure.
// Every figure is derived from the (scoped, non-deleted) transactions the caller
// passes — "statistics drawn from the working papers and transactions data". No
// clock, no DOM; unit-tested as plain data.
import type { Transaction } from '../../data/types';
import { normalizeDivisionFacet, normalizeDatasetFacet } from '../../domain/normalizeFacets';
import { linkState, linkReceived, linkFaulty } from '../../domain/links';
import { formatRand } from '../../lib/money';
import { gapTierOf, hasGap, isSelfDeclared, isOutstanding, type GapTier } from '../../domain/gaps';
import { NA_LEVELS } from '../../domain/dashboard';
import {
  DECK_DIVISION_ORDER,
} from './reportScope';

// The gap classifiers now live in domain/gaps — ONE definition shared by the
// dashboard finding-summary and this report module. Re-exported so existing
// importers of the report module (and its tests) keep resolving them here.
export { gapTierOf };
export type { GapTier };

function isReviewed(t: Transaction): boolean {
  return t.reviewed === true || String(t.reviewStatus ?? '').trim().toLowerCase() === 'reviewed';
}
/** Possible-IE Rand exposure of a single transaction (0 unless its level is Possible IE). */
function ieValue(t: Transaction): number {
  return gapTierOf(t.complianceLevel) === 'PFMA' ? (t.value ?? 0) : 0;
}
/**
 * Possible-IE Rand exposure carried by a SELF-DECLARED gap — the client slide's
 * "Rand value - Potential IE to be Self Declared". Resolved by declaration, but
 * still an exposure Eskom reports ("remains a risk to Eskom").
 */
function ieValueSelfDeclared(t: Transaction): number {
  return isSelfDeclared(t) ? ieValue(t) : 0;
}
/**
 * Possible-IE Rand exposure carried by a gap that is still OUTSTANDING — the client
 * slide's "Rand Value - Potential IE based on Unresolved Gaps".
 */
function ieValueOutstanding(t: Transaction): number {
  return isOutstanding(t) ? ieValue(t) : 0;
}

/**
 * `transaction → compliance level` accessor. The default reads the stored
 * `complianceLevel`; report callers pass the EFFECTIVE level (findings +
 * override rolled up, {@link effectiveComplianceByRef}) so the summary tables
 * reconcile with the KPI tiles (R5 #2).
 */
export type LevelOf = (t: Transaction) => string;

/**
 * Re-base a population on an effective-level accessor: clones each transaction
 * with `complianceLevel` replaced (blank effective level falls back to the
 * stored one). All the pure classifiers below then read the effective level
 * with no signature churn. Exported so buildReportModel can rebase ONCE and
 * feed the same based population to every builder (instead of five clones).
 */
export function withLevels(txs: readonly Transaction[], levelOf?: LevelOf): readonly Transaction[] {
  if (!levelOf) return txs;
  return txs.map((t) => ({ ...t, complianceLevel: levelOf(t) || t.complianceLevel }));
}

/** One summary row: the deck's population/review/gap columns for a group. */
export interface SummaryRow {
  key: string;
  population: number;
  reviewed: number;
  gapsIdentified: number;
  compliant: number;
  selfDeclared: number;
  outstanding: number;
  ieValue: number;
  // OpenText link tracking (owner feedback point 4 deck columns). Three-state
  // (Received / Pending / Outstanding) since Aug 2026; faulty is independent.
  linksReceived: number;
  linksPending: number;
  linksFaulty: number;
  linksOutstanding: number;
  // R5 #2 — the report tables' gap partition. The four category columns split
  // the CURRENTLY gap-carrying transactions disjointly (self-declared wins, the
  // rest by tier), and open/addressed split all identified gaps by lifecycle:
  //   gapsOpen + gapsAddressed = gapsIdentified
  // The tier columns sum to gapsIdentified ONLY on the transaction-grain path (no
  // findingRefs). When a findingRefs set is supplied (RTM Workstream I), a buyer
  // fix — a transaction now Compliant but still carrying a genuine finding — is
  // counted in gapsIdentified (and so in gapsAddressed) even though it has no
  // CURRENT tier, so tier + selfDeclaredGaps ≤ gapsIdentified for those rows.
  /** Outstanding gaps — still Open / In Progress (transaction basis). */
  gapsOpen: number;
  /** Closed gaps INCLUDING self-declaration closures (TK decision, R5 #2). */
  gapsAddressed: number;
  /** Possible IE (PFMA tier), excluding self-declared. */
  tierIe: number;
  /** Policies and Procedures tier, excluding self-declared. */
  tierPolicy: number;
  /** Housekeeping tier, excluding self-declared. */
  tierHousekeeping: number;
  /** Self-declared gap transactions (the rendered Self-Declared column). */
  selfDeclaredGaps: number;
  // --- V4 additions (Lindiwe's final template, 10 Sep 2026) ---------------
  /** Reviewed as a % of population (her arithmetic — her own slide-8 cell divides by population). NaN when population is 0. */
  pctReviewed: number;
  /** Reviewed as a % of links received, clamped to 100. NaN when no links received. Carried but not the rendered %. */
  pctReviewedOfLinks: number;
  /** Reviewed transactions whose effective level is Not Applicable. */
  notApplicable: number;
  /** Reviewed but none of compliant / self-declared / outstanding / NA (e.g. "To delete"). */
  unclassified: number;
  /** Links on file and not faulty (her "Links received" column). Disjoint with the two below. */
  linksReceivedClean: number;
  /** Link pending (marked provided, not captured) OR received-but-faulty (her "Faulty / empty"). */
  linksFaultyOrEmpty: number;
}

/**
 * Roll a transaction group up into one SummaryRow. `findingRefs`, when supplied,
 * is the set of referenceNos that carry at least one genuine finding (including
 * resolved ones) — so a buyer-fixed transaction (now Compliant, no current gap,
 * but a finding on record) still counts as an identified gap and, being no longer
 * outstanding, as an addressed one (RTM Workstream I). Omitted → pure transaction
 * grain, exactly as before.
 */
function summarise(key: string, txs: readonly Transaction[], findingRefs?: ReadonlySet<string>): SummaryRow {
  const row: SummaryRow = {
    key,
    population: 0,
    reviewed: 0,
    gapsIdentified: 0,
    compliant: 0,
    selfDeclared: 0,
    outstanding: 0,
    ieValue: 0,
    linksReceived: 0,
    linksPending: 0,
    linksFaulty: 0,
    linksOutstanding: 0,
    gapsOpen: 0,
    gapsAddressed: 0,
    tierIe: 0,
    tierPolicy: 0,
    tierHousekeeping: 0,
    selfDeclaredGaps: 0,
    pctReviewed: NaN,
    pctReviewedOfLinks: NaN,
    notApplicable: 0,
    unclassified: 0,
    linksReceivedClean: 0,
    linksFaultyOrEmpty: 0,
  };
  for (const t of txs) {
    row.population += 1;
    if (isReviewed(t)) row.reviewed += 1;
    // A gap is identified when the transaction CURRENTLY carries one (hasGap) OR it
    // has a genuine finding on record (findingRefs) — the latter catches a buyer fix
    // rolled up to Compliant, whose finding is kept as resolved (RTM Workstream I).
    const hasFinding = findingRefs != null && t.referenceNo != null && findingRefs.has(t.referenceNo);
    if (hasGap(t) || hasFinding) {
      row.gapsIdentified += 1;
      // Disjoint category partition of the CURRENT gap: self-declared first, else the
      // gap tier. A resolved gap (Compliant now, counted via its finding) has no
      // current tier, so it lands only in gapsIdentified/gapsAddressed below.
      if (isSelfDeclared(t)) row.selfDeclaredGaps += 1;
      else {
        const tier = gapTierOf(t.complianceLevel);
        if (tier === 'PFMA') row.tierIe += 1;
        else if (tier === 'Policy & Procedures') row.tierPolicy += 1;
        else if (tier === 'Housekeeping') row.tierHousekeeping += 1;
      }
    }
    if (t.complianceLevel === 'Compliant') row.compliant += 1;
    if (isSelfDeclared(t)) row.selfDeclared += 1;
    if (isOutstanding(t)) row.outstanding += 1;
    row.ieValue += ieValue(t);
    // Links (three-state, Aug 2026): received = a real link/URL is on file;
    // pending = marked provided but not captured yet; outstanding = neither.
    // Faulty is independent (a received link can still be faulty) and counts a
    // logged "Issue with links" answer — NOT a "No"/blank, which means no issue.
    const state = linkState(t);
    if (state === 'Received') row.linksReceived += 1;
    else if (state === 'Pending') row.linksPending += 1;
    else row.linksOutstanding += 1;
    if (linkFaulty(t)) row.linksFaulty += 1;
    // V4 disjoint link partition (sums to population): Outstanding | (Pending or
    // Received-and-faulty) | Received-clean.
    if (state === 'Outstanding') {
      /* counted in linksOutstanding above */
    } else if (state === 'Pending' || linkFaulty(t)) {
      row.linksFaultyOrEmpty += 1;
    } else {
      row.linksReceivedClean += 1;
    }
    // V4 reviewed-outcome residual buckets (for the I9 reconciliation): a reviewed
    // row that is neither compliant nor self-declared nor outstanding is either
    // Not Applicable or genuinely unclassified (e.g. a "To delete" level).
    if (isReviewed(t)) {
      const level = t.complianceLevel ?? '';
      if (NA_LEVELS.has(level)) row.notApplicable += 1;
      else if (level !== 'Compliant' && !isSelfDeclared(t) && !isOutstanding(t)) row.unclassified += 1;
    }
  }
  // Open + addressed partition the identified gaps (addressed = everything no
  // longer outstanding, self-declaration closures included per TK's R5 call).
  row.gapsOpen = row.outstanding;
  row.gapsAddressed = row.gapsIdentified - row.outstanding;
  // Ratios recomputed from the (possibly summed) counts, never accumulated.
  row.pctReviewed = row.population > 0 ? Math.round((row.reviewed / row.population) * 100) : NaN;
  row.pctReviewedOfLinks =
    row.linksReceived > 0 ? Math.min(100, Math.round((row.reviewed / row.linksReceived) * 100)) : NaN;
  return row;
}

/** Group + summarise by a key accessor, sorted by key, with a trailing "Total" row. */
function groupSummary(
  txs: readonly Transaction[],
  keyOf: (t: Transaction) => string,
  findingRefs?: ReadonlySet<string>,
): SummaryRow[] {
  const groups = new Map<string, Transaction[]>();
  for (const t of txs) {
    const k = keyOf(t) || 'Unspecified';
    const bucket = groups.get(k);
    if (bucket) bucket.push(t);
    else groups.set(k, [t]);
  }
  const rows = [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([k, group]) => summarise(k, group, findingRefs));
  if (rows.length > 0) rows.push({ ...summarise('Total', txs, findingRefs), key: 'Total' });
  return rows;
}

/**
 * Summary rows by dataset (Contracts / POs / LPOs / Task Order / Other) + Total.
 * `findingRefs` (refs carrying a genuine finding) makes gapsIdentified/gapsAddressed
 * count buyer fixes; omit for the pure transaction-grain roll-up (RTM Workstream I).
 */
export function datasetSummaryRows(
  txs: readonly Transaction[],
  levelOf?: LevelOf,
  findingRefs?: ReadonlySet<string>,
): SummaryRow[] {
  return groupSummary(withLevels(txs, levelOf), (t) => normalizeDatasetFacet(t.dataset) || (t.dataset ?? ''), findingRefs);
}

/** Summary rows by operating unit / division (canonicalised) + Total. */
export function divisionSummaryRows(
  txs: readonly Transaction[],
  levelOf?: LevelOf,
  findingRefs?: ReadonlySet<string>,
): SummaryRow[] {
  return groupSummary(withLevels(txs, levelOf), (t) => normalizeDivisionFacet(t.division) || (t.division ?? ''), findingRefs);
}

/** Summary rows by quarter (period) + Total. */
export function quarterSummaryRows(
  txs: readonly Transaction[],
  levelOf?: LevelOf,
  findingRefs?: ReadonlySet<string>,
): SummaryRow[] {
  return groupSummary(withLevels(txs, levelOf), (t) => String(t.period ?? '').trim(), findingRefs);
}

/**
 * THE R5 #2 client column set — the single source of truth for the summary
 * tables' rendered columns. Every surface (ReportsPage DataTable, PPTX, PDF)
 * derives its header + cells from this list, so the three deliverables cannot
 * drift apart on the exact columns the client specified.
 */
export interface SummaryColumnDef {
  key: string;
  header: string;
  value: (r: SummaryRow) => string;
}
const num = (n: number): string => n.toLocaleString('en-ZA');
/** Rand cell for the deck tables — the app-wide `R 1 234 567` display. */
const rand = (n: number): string => formatRand(n) || 'R 0';

/**
 * One column of a {@link GapBreakdownRow} table (the two R7 client slides). Mirrors
 * {@link SummaryColumnDef}, plus an optional `group`: consecutive columns sharing a
 * group are rendered under one merged heading spanning them, which is how the
 * client's "Breakdown of Self Declarations to date" / "Breakdown of outstanding
 * gaps" two-row header works.
 */
export interface GapColumnDef {
  key: string;
  header: string;
  group?: string;
  value: (r: GapBreakdownRow) => string;
}
export const SUMMARY_TABLE_COLUMNS: readonly SummaryColumnDef[] = [
  { key: 'population', header: 'Total Population', value: (r) => num(r.population) },
  { key: 'linksReceived', header: 'Links Received', value: (r) => num(r.linksReceived) },
  { key: 'gapsOpen', header: 'Gaps Identified (Open)', value: (r) => num(r.gapsOpen) },
  { key: 'gapsAddressed', header: 'Gaps Addressed (Closed)', value: (r) => num(r.gapsAddressed) },
  { key: 'tierIe', header: 'Possible IE', value: (r) => num(r.tierIe) },
  { key: 'tierPolicy', header: 'Policies and Procedures', value: (r) => num(r.tierPolicy) },
  { key: 'tierHousekeeping', header: 'Housekeeping', value: (r) => num(r.tierHousekeeping) },
  { key: 'selfDeclaredGaps', header: 'Self-Declared', value: (r) => num(r.selfDeclaredGaps) },
];

/**
 * Rendered under the two summary tables on every surface (R5 #2): the tables
 * count TRANSACTIONS by effective compliance level, while the dashboard's
 * "Gaps open" tile counts individual finding LINES — different units that need
 * not match (the R4 breach-split precedent).
 */
export const SUMMARY_GAPS_FOOTNOTE =
  'These columns count TRANSACTIONS (records) by their effective compliance level — how many of the population ' +
  'carry, or have closed, a gap. The headline Findings count INDIVIDUAL findings still outstanding ' +
  '(distinct non-compliant criteria, self-declared and resolved excluded): a record with several findings adds ' +
  'one here but several there.';

/**
 * Column set for the client's "FY27 – DIVISIONAL SUMMARY REPORT" slide, verbatim in
 * their order and wording (27 Jul 2026). Same single-source-of-truth discipline as
 * {@link SUMMARY_TABLE_COLUMNS}: screen, PPTX and PDF all derive from this list.
 *
 * Note "Total of gaps addressed including self declaration" is `totalAddressed`
 * (= addressed + selfDeclared) — Lindiwe's "when we account for a gap closed … it's
 * equal to self-declared transactions plus the closed compliant transactions".
 */
export const DIVISIONAL_SUMMARY_COLUMNS: readonly GapColumnDef[] = [
  { key: 'population', header: 'Total Population', value: (r) => num(r.population) },
  { key: 'reviewed', header: 'No# Reviewed', value: (r) => num(r.reviewed) },
  { key: 'linksReceived', header: 'Links Received', value: (r) => num(r.linksReceived) },
  { key: 'gapsIdentified', header: 'Gaps Identified', value: (r) => num(r.gapsIdentified) },
  { key: 'addressed', header: 'Gaps Addressed: Compliant', value: (r) => num(r.addressed) },
  {
    key: 'selfDeclared',
    header: 'Gaps Addressed/ to be Addressed: Self Declaration',
    value: (r) => num(r.selfDeclared),
  },
  {
    key: 'totalAddressed',
    header: 'Total of gaps addressed including self declaration',
    value: (r) => num(r.totalAddressed),
  },
  { key: 'outstanding', header: 'Outstanding Gaps', value: (r) => num(r.outstanding) },
];

/**
 * Column set for the client's "SUMMARY OF GAPS AND SELF-DECLARATION" slide, verbatim
 * (27 Jul 2026). `group` carries the merged top-row heading that spans the three
 * tier columns beneath it, so the PPTX/PDF can render the two-row header the client
 * uses without hard-coding spans at the call site.
 */
export const GAPS_SELF_DECLARATION_COLUMNS: readonly GapColumnDef[] = [
  { key: 'population', header: 'Total Population', value: (r) => num(r.population) },
  { key: 'reviewed', header: 'Total Reviewed', value: (r) => num(r.reviewed) },
  { key: 'selfDeclared', header: 'Self Declaration', value: (r) => num(r.selfDeclared) },
  {
    key: 'sdPfma',
    header: 'PFMA',
    group: 'Breakdown of Self Declarations to date',
    value: (r) => num(r.selfTiers.pfma),
  },
  {
    key: 'sdPolicy',
    header: 'Policy and Procedures',
    group: 'Breakdown of Self Declarations to date',
    value: (r) => num(r.selfTiers.policy),
  },
  {
    key: 'sdHousekeeping',
    header: 'Housekeeping',
    group: 'Breakdown of Self Declarations to date',
    value: (r) => num(r.selfTiers.housekeeping),
  },
  {
    key: 'ieSelfDeclared',
    header: 'Rand value - Potential IE to be Self Declared',
    value: (r) => rand(r.ieSelfDeclared),
  },
  { key: 'outstanding', header: 'Outstanding Gaps', value: (r) => num(r.outstanding) },
  {
    key: 'outPfma',
    header: 'PFMA',
    group: 'Breakdown of outstanding gaps',
    value: (r) => num(r.outTiers.pfma),
  },
  {
    key: 'outPolicy',
    header: 'Policy and Procedures',
    group: 'Breakdown of outstanding gaps',
    value: (r) => num(r.outTiers.policy),
  },
  {
    key: 'outHousekeeping',
    header: 'Housekeeping',
    group: 'Breakdown of outstanding gaps',
    value: (r) => num(r.outTiers.housekeeping),
  },
  {
    key: 'ieOutstanding',
    header: 'Rand Value - Potential IE based on Unresolved Gaps',
    value: (r) => rand(r.ieOutstanding),
  },
];

/**
 * The narrative line above the gaps/self-declaration table, in the client's own
 * framing: a self-declared gap is closed, yet the Rand exposure it carries "remains
 * a risk to Eskom". Derived from the Total row so it can never contradict the table.
 */
export function gapsSelfDeclarationNarrative(rows: readonly GapBreakdownRow[]): string {
  const total = rows.find((r) => r.key === 'Total') ?? rows[rows.length - 1];
  if (!total) return '';
  return (
    `A total population of ${num(total.population)} transactions were identified with ` +
    `${num(total.reviewed)} reviews completed. Self-declared Potential Irregular Expenditure (IE) of ` +
    `${rand(total.ieSelfDeclared)} remains a risk to Eskom.`
  );
}

/** Gap counts split by tier (PFMA / Policy & Procedures / Housekeeping). */
export interface TierSplit {
  tier: GapTier;
  selfDeclared: number;
  outstanding: number;
}

export function gapTierSplit(txs: readonly Transaction[], levelOf?: LevelOf): TierSplit[] {
  const tiers: GapTier[] = ['PFMA', 'Policy & Procedures', 'Housekeeping'];
  const based = withLevels(txs, levelOf);
  return tiers.map((tier) => {
    let selfDeclared = 0;
    let outstanding = 0;
    for (const t of based) {
      if (gapTierOf(t.complianceLevel) !== tier) continue;
      if (isSelfDeclared(t)) selfDeclared += 1;
      else if (isOutstanding(t)) outstanding += 1;
    }
    return { tier, selfDeclared, outstanding };
  });
}

// --- Per-division Review Update (R3 #4 — client deck slides 13–18) ----------
// One block per division: the dataset-level summary (the same SummaryRow model
// as the consolidated tables) plus a gaps/self-declaration breakdown with
// PFMA / Policy & Procedures / Housekeeping splits — the two tables on the
// client's per-division "Review Update" slides.

/** Per-tier counts for a breakdown cell group. */
export interface TierCounts {
  pfma: number;
  policy: number;
  housekeeping: number;
}

/** One gaps/self-declaration breakdown row (per dataset within a division). */
export interface GapBreakdownRow {
  key: string;
  gapsIdentified: number;
  /** Gaps addressed: Compliant. */
  addressed: number;
  selfDeclared: number;
  selfTiers: TierCounts;
  /** addressed + selfDeclared (the client's "Total of gaps addressed incl. SD"). */
  totalAddressed: number;
  outstanding: number;
  outTiers: TierCounts;
  // R7 (27 Jul 2026) — the two new client slides put population/review/link columns
  // and the two Potential-IE Rand columns on the SAME row as the gap breakdown, so
  // they live here rather than forcing the slide to join two row models.
  /** Every transaction in the group, gap-carrying or not. */
  population: number;
  /** Transactions a reviewer has signed off. */
  reviewed: number;
  /** OpenText link provided (or captured as a link/URL) — same rule as SummaryRow. */
  linksReceived: number;
  /** "Rand value - Potential IE to be Self Declared". */
  ieSelfDeclared: number;
  /** "Rand Value - Potential IE based on Unresolved Gaps". */
  ieOutstanding: number;
}

/** One division's Review Update: dataset summary + gaps breakdown (each + Total). */
export interface DivisionReviewUpdate {
  division: string;
  summary: SummaryRow[];
  gaps: GapBreakdownRow[];
}

function emptyTiers(): TierCounts {
  return { pfma: 0, policy: 0, housekeeping: 0 };
}

function bumpTier(counts: TierCounts, tier: GapTier | null): void {
  if (tier === 'PFMA') counts.pfma += 1;
  else if (tier === 'Policy & Procedures') counts.policy += 1;
  else if (tier === 'Housekeeping') counts.housekeeping += 1;
}

function gapBreakdown(key: string, txs: readonly Transaction[]): GapBreakdownRow {
  const row: GapBreakdownRow = {
    key,
    gapsIdentified: 0,
    addressed: 0,
    selfDeclared: 0,
    selfTiers: emptyTiers(),
    totalAddressed: 0,
    outstanding: 0,
    outTiers: emptyTiers(),
    population: 0,
    reviewed: 0,
    linksReceived: 0,
    ieSelfDeclared: 0,
    ieOutstanding: 0,
  };
  for (const t of txs) {
    // Population / review / links / Rand exposure span the WHOLE group, so they are
    // counted before the gap filter below.
    row.population += 1;
    if (isReviewed(t)) row.reviewed += 1;
    if (linkReceived(t)) row.linksReceived += 1;
    row.ieSelfDeclared += ieValueSelfDeclared(t);
    row.ieOutstanding += ieValueOutstanding(t);

    if (!hasGap(t)) continue;
    row.gapsIdentified += 1;
    const tier = gapTierOf(t.complianceLevel);
    if (isSelfDeclared(t)) {
      row.selfDeclared += 1;
      bumpTier(row.selfTiers, tier);
    } else if (isOutstanding(t)) {
      row.outstanding += 1;
      bumpTier(row.outTiers, tier);
    } else {
      // A gap that is neither self-declared nor outstanding has been addressed
      // (closed/resolved/compliant).
      row.addressed += 1;
    }
  }
  row.totalAddressed = row.addressed + row.selfDeclared;
  return row;
}

/**
 * Gap/self-declaration breakdown by operating unit (canonical division) + Total —
 * the row model behind BOTH new client slides (27 Jul 2026):
 *
 *   • "FY27 – DIVISIONAL SUMMARY REPORT" — population, reviewed, links received,
 *     gaps identified, addressed-compliant, addressed-by-self-declaration, the two
 *     added together, and what is still outstanding.
 *   • "FY26: SUMMARY OF GAPS AND SELF-DECLARATION" — the same spine plus the
 *     PFMA/Policy/Housekeeping breakdown of each of the self-declared and
 *     outstanding sets, and the two Potential-IE Rand columns.
 *
 * Canonicalised via {@link normalizeDivisionFacet}, so the live data's "GX- Head
 * Office" and "Gx" fold together (they are separate strings in production) exactly
 * as every other division roll-up folds them.
 */
export function divisionGapBreakdownRows(
  txs: readonly Transaction[],
  levelOf?: LevelOf,
): GapBreakdownRow[] {
  const based = withLevels(txs, levelOf);
  const groups = new Map<string, Transaction[]>();
  for (const t of based) {
    const k = normalizeDivisionFacet(t.division) || (t.division ?? '') || 'Unspecified';
    const bucket = groups.get(k);
    if (bucket) bucket.push(t);
    else groups.set(k, [t]);
  }
  const rows = [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([k, group]) => gapBreakdown(k, group));
  if (rows.length > 0) rows.push({ ...gapBreakdown('Total', based), key: 'Total' });
  return rows;
}

/**
 * Build the per-division Review Update blocks (sorted by division name). Each
 * division's rows group by dataset, with a trailing Total row — matching the
 * client deck's per-division slides.
 */
export function divisionReviewUpdates(txs: readonly Transaction[], levelOf?: LevelOf): DivisionReviewUpdate[] {
  const byDivision = new Map<string, Transaction[]>();
  for (const t of withLevels(txs, levelOf)) {
    const d = normalizeDivisionFacet(t.division) || (t.division ?? '') || 'Unspecified';
    const bucket = byDivision.get(d);
    if (bucket) bucket.push(t);
    else byDivision.set(d, [t]);
  }
  return [...byDivision.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([division, group]) => {
      const datasetOf = (t: Transaction): string =>
        normalizeDatasetFacet(t.dataset) || (t.dataset ?? '') || 'Unspecified';
      const datasets = new Map<string, Transaction[]>();
      for (const t of group) {
        const k = datasetOf(t);
        const bucket = datasets.get(k);
        if (bucket) bucket.push(t);
        else datasets.set(k, [t]);
      }
      const keys = [...datasets.keys()].sort((a, b) => a.localeCompare(b));
      const summary = keys.map((k) => ({ ...summarise(k, datasets.get(k)!) }));
      summary.push({ ...summarise('Total', group), key: 'Total' });
      const gaps = keys.map((k) => gapBreakdown(k, datasets.get(k)!));
      gaps.push({ ...gapBreakdown('Total', group), key: 'Total' });
      return { division, summary, gaps };
    });
}

// ===========================================================================
// V4 client template (Lindiwe's final deck, 10 Sep 2026) — new column
// registries with DECLARED header accents (the appendix keeps headerAccent()).
// The existing registries above are untouched so the analytics appendix and its
// pin tests keep working.
// ===========================================================================

/** Percentage cell — '-' when the ratio is undefined (no denominator). */
const pct = (n: number): string => (Number.isNaN(n) ? '-' : `${n}%`);

/** Declared header accent for a V4 column (maps to a fill + text colour in eskomDeck). */
export type V4Accent = 'green' | 'amber' | 'amberSoft' | 'red' | 'redBright' | 'yellow' | 'orange';

export interface V4ColumnDef extends SummaryColumnDef {
  accent?: V4Accent;
  /** Per-cell body fill hex (no '#'), e.g. the two Potential-IE Rand columns. */
  bodyFill?: (r: SummaryRow) => string | undefined;
}
export interface V4GapColumnDef extends GapColumnDef {
  accent?: V4Accent;
  bodyFill?: (r: GapBreakdownRow) => string | undefined;
}

/** Slide 2 — SUMMARY OF GAPS AND SELF-DECLARATION (key column: Operating Unit/Power station). */
export const GAPS_SD_V4_COLUMNS: readonly V4GapColumnDef[] = [
  { key: 'population', header: 'Total Population', value: (r) => num(r.population) },
  { key: 'reviewed', header: 'Total Reviewed', value: (r) => num(r.reviewed) },
  { key: 'selfDeclared', header: 'Self Declaration', accent: 'amberSoft', value: (r) => num(r.selfDeclared) },
  {
    key: 'ieSelfDeclared',
    header: 'Rand value - Potential IE to be Self Declared',
    accent: 'amberSoft',
    bodyFill: () => 'C97A00',
    value: (r) => rand(r.ieSelfDeclared),
  },
  { key: 'outstanding', header: 'Outstanding Gaps', accent: 'red', value: (r) => num(r.outstanding) },
  { key: 'outPfma', header: 'PFMA', group: 'Breakdown of outstanding gaps', accent: 'red', value: (r) => num(r.outTiers.pfma) },
  {
    key: 'outPolicy',
    header: 'Policy and Procedures',
    group: 'Breakdown of outstanding gaps',
    accent: 'red',
    value: (r) => num(r.outTiers.policy),
  },
  {
    key: 'outHousekeeping',
    header: 'Housekeeping',
    group: 'Breakdown of outstanding gaps',
    accent: 'red',
    value: (r) => num(r.outTiers.housekeeping),
  },
  {
    key: 'ieOutstanding',
    header: 'Rand Value - Potential IE based on Unresolved Gaps',
    accent: 'red',
    bodyFill: () => '96330F',
    value: (r) => rand(r.ieOutstanding),
  },
];

/** Slides 3 and per-quarter — DIVISIONAL SUMMARY (key column: Division). */
export const DIVISIONAL_V4_COLUMNS: readonly V4ColumnDef[] = [
  { key: 'population', header: 'Population', value: (r) => num(r.population) },
  { key: 'linksReceivedClean', header: 'Links received', value: (r) => num(r.linksReceivedClean) },
  { key: 'linksFaultyOrEmpty', header: 'Faulty / empty', value: (r) => num(r.linksFaultyOrEmpty) },
  { key: 'linksOutstanding', header: 'Links outstanding', value: (r) => num(r.linksOutstanding) },
  { key: 'reviewed', header: 'Reviewed', value: (r) => num(r.reviewed) },
  { key: 'pctReviewed', header: '% Reviewed of Links Received', value: (r) => pct(r.pctReviewed) },
  { key: 'compliant', header: 'Compliant', accent: 'green', value: (r) => num(r.compliant) },
  { key: 'selfDeclared', header: 'Self-declared', accent: 'amber', value: (r) => num(r.selfDeclared) },
  { key: 'outstanding', header: 'Gaps Outstanding', accent: 'red', value: (r) => num(r.outstanding) },
  // Anomalies umbrella (Lindiwe, 15 Sep 2026) = Not Applicable + the 'To delete'/'Deleted'
  // residual (unclassified). Rendering it makes the reviewed-outcome columns exhaustive and
  // matches the dashboard's Anomalies tile, so the two surfaces reconcile column-for-column.
  { key: 'anomalies', header: 'Anomalies', value: (r) => num(r.notApplicable + r.unclassified) },
];

/** Slide 4 — SUMMARY BY SOURCE (key column: Transaction Type). */
export const SOURCE_V4_COLUMNS: readonly V4ColumnDef[] = [
  { key: 'population', header: 'Population', value: (r) => num(r.population) },
  { key: 'linksReceivedClean', header: 'Links Received', value: (r) => num(r.linksReceivedClean) },
  { key: 'linksFaultyOrEmpty', header: 'Faulty/Empty', value: (r) => num(r.linksFaultyOrEmpty) },
  { key: 'linksOutstanding', header: 'Links outstanding', value: (r) => num(r.linksOutstanding) },
  { key: 'reviewed', header: 'Reviewed', value: (r) => num(r.reviewed) },
  { key: 'pctReviewed', header: '% Reviewed', value: (r) => pct(r.pctReviewed) },
  { key: 'outstanding', header: 'Outstanding Gaps', accent: 'redBright', value: (r) => num(r.outstanding) },
  { key: 'tierHousekeeping', header: 'House Keeping', accent: 'yellow', value: (r) => num(r.tierHousekeeping) },
  { key: 'tierPolicy', header: 'Policies & Procedures', accent: 'orange', value: (r) => num(r.tierPolicy) },
  { key: 'tierIe', header: 'Possible IE', accent: 'red', value: (r) => num(r.tierIe) },
  { key: 'selfDeclaredGaps', header: 'To be Self-declared', accent: 'amber', value: (r) => num(r.selfDeclaredGaps) },
  { key: 'compliant', header: 'Compliant', accent: 'green', value: (r) => num(r.compliant) },
];

/** Per-division REVIEW UPDATE tables (key column: Transaction Type). One set for all six slides. */
export const REVIEW_UPDATE_V4_COLUMNS: readonly V4ColumnDef[] = [
  { key: 'population', header: 'Population', value: (r) => num(r.population) },
  { key: 'linksReceivedClean', header: 'Links received', value: (r) => num(r.linksReceivedClean) },
  { key: 'linksFaultyOrEmpty', header: 'Faulty', value: (r) => num(r.linksFaultyOrEmpty) },
  { key: 'linksOutstanding', header: 'Links outstanding', value: (r) => num(r.linksOutstanding) },
  { key: 'reviewed', header: 'Reviewed', value: (r) => num(r.reviewed) },
  { key: 'pctReviewed', header: '% Reviewed', value: (r) => pct(r.pctReviewed) },
  { key: 'compliant', header: 'Compliant', accent: 'green', value: (r) => num(r.compliant) },
  { key: 'selfDeclared', header: 'Self-decl.', accent: 'amber', value: (r) => num(r.selfDeclared) },
  { key: 'outstanding', header: 'Gaps Outstanding', accent: 'red', value: (r) => num(r.outstanding) },
  { key: 'anomalies', header: 'Anomalies', value: (r) => num(r.notApplicable + r.unclassified) },
];

/** Slide 7 — REPORTS TO MANAGEMENT status columns (key column: Operating Unit). */
export const RTM_STATUS_COLUMNS = ['RTM Submitted', 'RTM signed off'] as const;

/** Slide 6 — the five KPI cards, read from the cumulative divisional Total row only. */
export const KPI_CARDS_V4: readonly {
  key: keyof SummaryRow;
  label: string;
  fill: string;
  ink: string;
}[] = [
  { key: 'population', label: 'Total population', fill: 'D9E2EC', ink: '0F1F2E' },
  { key: 'reviewed', label: 'Reviewed', fill: '002060', ink: 'FFFFFF' },
  { key: 'compliant', label: 'Compliant', fill: '00B050', ink: 'FFFFFF' },
  { key: 'selfDeclared', label: 'To be Self-declared', fill: 'FFC000', ink: '0F1F2E' },
  { key: 'tierIe', label: 'Possible IE', fill: 'C00000', ink: 'FFFFFF' },
];

/**
 * Every additive numeric field of a SummaryRow — summed by {@link sumRows}; the
 * two ratios are recomputed, never summed. A compile-time check below asserts the
 * union is exhaustive so a new SummaryRow number cannot silently escape the sum.
 */
export const SUMMARY_ADDITIVE_FIELDS = [
  'population',
  'reviewed',
  'gapsIdentified',
  'compliant',
  'selfDeclared',
  'outstanding',
  'ieValue',
  'linksReceived',
  'linksPending',
  'linksFaulty',
  'linksOutstanding',
  'linksReceivedClean',
  'linksFaultyOrEmpty',
  'gapsOpen',
  'gapsAddressed',
  'tierIe',
  'tierPolicy',
  'tierHousekeeping',
  'selfDeclaredGaps',
  'notApplicable',
  'unclassified',
] as const;

// Compile-time exhaustiveness: every numeric SummaryRow key except the two
// ratios must appear in SUMMARY_ADDITIVE_FIELDS.
type _RatioKeys = 'pctReviewed' | 'pctReviewedOfLinks';
type _NumericSummaryKeys = {
  [K in keyof SummaryRow]: SummaryRow[K] extends number ? K : never;
}[keyof SummaryRow];
type _AdditiveKeys = (typeof SUMMARY_ADDITIVE_FIELDS)[number];
// If a numeric field is added to SummaryRow without adding it here (or to the
// ratio set), this assignment fails to typecheck.
const _additiveExhaustive: Exclude<_NumericSummaryKeys, _AdditiveKeys | _RatioKeys> extends never
  ? true
  : false = true;
void _additiveExhaustive;

/** Sum a set of SummaryRows into one row (additive fields summed, ratios recomputed). */
export function sumRows(rows: readonly SummaryRow[], key = 'Total'): SummaryRow {
  const acc = { ...summarise(key, []), key };
  for (const f of SUMMARY_ADDITIVE_FIELDS) {
    acc[f] = rows.reduce((s, r) => s + r[f], 0);
  }
  acc.pctReviewed = acc.population > 0 ? Math.round((acc.reviewed / acc.population) * 100) : NaN;
  acc.pctReviewedOfLinks =
    acc.linksReceived > 0 ? Math.min(100, Math.round((acc.reviewed / acc.linksReceived) * 100)) : NaN;
  return acc;
}

/**
 * Re-order division rows to {@link DECK_DIVISION_ORDER}, zero-filling any absent
 * division and keeping the Total row last. Non-canonical divisions present in the
 * data (e.g. CCD, Unspecified) follow the canonical set. So every divisional table
 * lists the same divisions in the same order — the basis for the quarter-slices-
 * partition-the-cumulative invariant.
 */
function reorderDivisions<T extends { key: string }>(rows: readonly T[], emptyOf: (key: string) => T): T[] {
  const byKey = new Map(rows.map((r) => [r.key, r]));
  const total = byKey.get('Total');
  const ordered = DECK_DIVISION_ORDER.map((d) => byKey.get(d) ?? emptyOf(d));
  const extra = rows.filter(
    (r) => r.key !== 'Total' && !DECK_DIVISION_ORDER.includes(r.key),
  );
  const body = [...ordered, ...extra];
  return total ? [...body, total] : body;
}

/** Division SummaryRows in canonical order, zero-filled. */
export function canonicalDivisionSummaryRows(rows: readonly SummaryRow[]): SummaryRow[] {
  return reorderDivisions(rows, (key) => ({ ...summarise(key, []), key }));
}

/** Division GapBreakdownRows in canonical order, zero-filled. */
export function canonicalDivisionGapRows(rows: readonly GapBreakdownRow[]): GapBreakdownRow[] {
  return reorderDivisions(rows, (key) => ({ ...gapBreakdown(key, []), key }));
}
